import { BriefDraftSchema, briefValidationProblem } from "@prototypical/types";
import { ApiError, request, requestSiteMutation } from "./client";
import type {
  BriefAttachment,
  BriefDraft,
  BriefRecord,
  BusinessSearchResult,
  BusinessType,
  HarvestSummary,
  InspoPreview,
  Site,
  SubdomainCheck,
} from "./types";

export const startApi = {
  searchBusinesses: (name: string, address?: string) =>
    request<BusinessSearchResult>("/api/start/search", {
      method: "POST",
      body: JSON.stringify({ name, address }),
    }),
  prefillFromBusiness: (harvestId: string) =>
    request<{ brief: BriefDraft; harvestSummary: HarvestSummary }>("/api/start/prefill", {
      method: "POST",
      body: JSON.stringify({ harvestId }),
    }),
  createBrief: async (brief: Omit<BriefDraft, "ownerId">) => {
    const parsed = BriefDraftSchema.safeParse(brief);
    if (!parsed.success) throw new ApiError(400, briefValidationProblem(parsed.error.issues));
    return request<{ brief: BriefRecord; remainingToday: number }>("/api/start/brief", {
      method: "POST",
      body: JSON.stringify(parsed.data),
    });
  },
  startNewSite: (params: { subdomain: string; briefId: string; kickBuild?: boolean; requestId?: string }) =>
    requestSiteMutation<{ site: Site; jobId: string | null; idempotent?: boolean }>("/api/start/site", {
      method: "POST",
      body: JSON.stringify(params),
    }),
  checkSubdomain: (q: string) =>
    request<SubdomainCheck>(`/api/start/subdomain-check?q=${encodeURIComponent(q)}`),
  signUpload: (params: { name: string; mimeType: string; sizeBytes: number; tag?: string }) =>
    request<{ signedPutUrl: string; gcsPath: string; mimeType?: string; requiredHeaders: Record<string, string>; tag?: string }>(
      "/api/start/upload-sign",
      { method: "POST", body: JSON.stringify(params) },
    ),
  extractZip: (zipGcsPath: string) =>
    request<{
      attachments: BriefAttachment[];
      skipped: Array<{ name: string; reason: string }>;
    }>("/api/start/extract-zip", {
      method: "POST",
      body: JSON.stringify({ zipGcsPath }),
    }),
  getInspoPreview: (url: string) =>
    request<InspoPreview>(`/api/start/inspo-preview?url=${encodeURIComponent(url)}`),
  draftDescription: (params: { businessName: string; tagline?: string; type: BusinessType }) =>
    request<{ suggestions: string[] }>("/api/start/draft-description", {
      method: "POST",
      body: JSON.stringify(params),
    }),
  consumeOnboardingIntent: (intentId: string) =>
    request<{ prompt: string }>(`/api/onboarding/intents/${encodeURIComponent(intentId)}/consume`, {
      method: "POST",
    }),
};
